import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type B3DsInputBackground = 'white' | 'grey';
export type B3DsInputMask =
  | 'text'
  | 'cpf'
  | 'cnpj'
  | 'cnpj-alpha'
  | 'phone'
  | 'cellphone'
  | 'date'
  | 'password'
  | 'cep'
  | 'currency'
  | 'account'
  | 'psa-account'
  | 'instrument'
  | 'integer'
  | 'link';

type B3DsInputTextElement = HTMLElement & {
  value?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  hint?: string;
  error?: boolean;
  mask?: string;
  background?: B3DsInputBackground;
  id?: string;
  maxlength?: number;
  map?: Record<string, { pattern: unknown; placeholder: string; pipe?: unknown }>;
};

const CUSTOM_MASKS: Record<
  'account' | 'psa-account' | 'instrument' | 'integer' | 'link' | 'cnpj-alpha',
  { pattern: unknown; placeholder: string }
> = {
  account: {
    pattern: '00000.00-0',
    placeholder: '00000.00-0',
  },
  'psa-account': {
    pattern: '00000.40-0',
    placeholder: '00000.40-0',
  },
  /** CNPJ alfanumérico (Receita Federal): aceita A-Z e 0-9 com máscara XX.XXX.XXX/XXXX-XX. */
  'cnpj-alpha': {
    pattern: '**.***.***/****-**',
    placeholder: '00.000.000/0000-00',
  },
  instrument: {
    pattern: /^[A-Za-z0-9]{0,12}$/,
    placeholder: '',
  },
  integer: {
    pattern: /^\d*$/,
    placeholder: '',
  },
  link: {
    pattern: /.*/,
    placeholder: 'https://www.exemplo.com.br',
  },
};

@Directive({
  selector: 'b3-input-text[formControlName], b3-input-text[ngModel]',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => B3DsInputTextDirective),
      multi: true,
    },
  ],
})
export class B3DsInputTextDirective implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() error = false;
  @Input() mask: B3DsInputMask = 'text';
  @Input() background: B3DsInputBackground = 'white';
  @Input() inputId = '';
  @Input() disabled = false;
  @Input() autocomplete = '';
  @Input() maxlength?: number;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private value = '';
  private formDisabled = false;
  private readonly onCaptureKeydown = (event: Event) => this.onHostKeydown(event as KeyboardEvent);
  private readonly onCaptureBeforeInput = (event: Event) => this.onBeforeInput(event as InputEvent);
  private readonly onCapturePaste = (event: Event) => this.onPaste(event as ClipboardEvent);

  constructor(
    private readonly elementRef: ElementRef<B3DsInputTextElement>,
    private readonly renderer: Renderer2,
  ) {
    this.registerCustomMasks();
  }

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;
    host.addEventListener('keydown', this.onCaptureKeydown, true);
    host.addEventListener('beforeinput', this.onCaptureBeforeInput, true);
    host.addEventListener('paste', this.onCapturePaste, true);
    this.syncElementState();
  }

  ngOnDestroy(): void {
    const host = this.elementRef.nativeElement;
    host.removeEventListener('keydown', this.onCaptureKeydown, true);
    host.removeEventListener('beforeinput', this.onCaptureBeforeInput, true);
    host.removeEventListener('paste', this.onCapturePaste, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['label'] ||
      changes['placeholder'] ||
      changes['hint'] ||
      changes['error'] ||
      changes['mask'] ||
      changes['background'] ||
      changes['inputId'] ||
      changes['disabled'] ||
      changes['autocomplete'] ||
      changes['maxlength']
    ) {
      this.syncElementState();
    }
  }

  writeValue(value: string | null | unknown): void {
    if (value == null) {
      this.value = '';
    } else if (typeof value === 'string') {
      this.value = this.applyMaxLength(value);
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      this.value = this.applyMaxLength(String(value));
    } else {
      this.value = '';
    }

    this.syncElementState();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.syncElementState();
  }

  @HostListener('b3-input', ['$event'])
  onB3Input(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: string }>;
    const rawValue = customEvent.detail?.value ?? '';
    const truncatedValue = this.applyMaxLength(rawValue);

    this.value = truncatedValue;
    this.syncElementState();

    if (rawValue !== truncatedValue) {
      // Force the web component to drop overflow characters in the visible input.
      this.elementRef.nativeElement.value = truncatedValue;
      this.renderer.setAttribute(this.elementRef.nativeElement, 'value', truncatedValue);
      this.syncInternalInputMaxLength();
    }

    this.onChange(this.value);
  }

  @HostListener('b3-change', ['$event'])
  onB3Change(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: string }>;
    const newValue = this.applyMaxLength(customEvent.detail?.value ?? '');
    const previousNormalized = this.normalizeValue(this.value);
    const newNormalized = this.normalizeValue(newValue);

    this.value = newValue;
    this.syncElementState();

    if (newNormalized !== previousNormalized) {
      this.onChange(this.value);
    }

    this.onTouched();
  }

  @HostListener('b3-blur')
  onB3Blur(): void {
    this.onTouched();
  }

  private onHostKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onTouched();
      this.elementRef.nativeElement.dispatchEvent(
        new CustomEvent('b3-enter', { bubbles: true, composed: true, detail: { value: this.value } }),
      );
      return;
    }

    if (!this.shouldBlockKeydown(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  private onBeforeInput(event: InputEvent): void {
    if (this.maxlength == null || this.maxlength <= 0) {
      return;
    }

    if (event.inputType?.startsWith('delete') || event.inputType === 'historyUndo') {
      return;
    }

    const data = event.data ?? '';
    if (!data) {
      return;
    }

    const input = this.getInternalInput();
    const currentValue = input?.value ?? this.value;
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const nextLength = currentValue.length - (selectionEnd - selectionStart) + data.length;

    if (nextLength > this.maxlength) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private onPaste(event: ClipboardEvent): void {
    if (this.maxlength == null || this.maxlength <= 0) {
      return;
    }

    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!pasted) {
      return;
    }

    const input = this.getInternalInput();
    const currentValue = input?.value ?? this.value;
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const nextValue = (
      currentValue.slice(0, selectionStart)
      + pasted
      + currentValue.slice(selectionEnd)
    ).slice(0, this.maxlength);

    event.preventDefault();
    event.stopPropagation();

    this.value = nextValue;
    this.syncElementState();
    this.onChange(this.value);
  }

  private registerCustomMasks(): void {
    const element = this.elementRef.nativeElement;
    const clonedMasks = Object.fromEntries(
      Object.entries(CUSTOM_MASKS).map(([key, config]) => [key, { ...config }]),
    ) as typeof CUSTOM_MASKS;

    element.map = {
      ...(element.map ?? {}),
      ...clonedMasks,
    };
  }

  private syncCustomMaskPlaceholder(element: B3DsInputTextElement): void {
    if (!(this.mask in CUSTOM_MASKS)) {
      return;
    }

    const maskKey = this.mask as keyof typeof CUSTOM_MASKS;
    const defaultMask = CUSTOM_MASKS[maskKey];
    const currentMask = element.map?.[maskKey] ?? defaultMask;

    element.map = {
      ...(element.map ?? {}),
      [maskKey]: {
        ...currentMask,
        placeholder: this.placeholder || defaultMask.placeholder,
      },
    };
  }

  private syncElementState(): void {
    const element = this.elementRef.nativeElement;
    this.registerCustomMasks();
    element.value = this.value;
    element.disabled = this.disabled || this.formDisabled;
    element.error = this.error;
    element.background = this.background;
    element.mask = this.mask;
    this.syncCustomMaskPlaceholder(element);
    this.syncMaskMaxLength(element);

    this.setOptionalAttribute(element, 'label', this.label);
    this.setOptionalAttribute(element, 'placeholder', this.placeholder);
    this.setOptionalAttribute(element, 'hint', this.hint);
    this.setOptionalAttribute(element, 'mask', this.mask);
    this.setOptionalAttribute(element, 'background', this.background);
    this.setOptionalAttribute(element, 'autocomplete', this.autocomplete);
    this.setOptionalAttribute(element, 'value', this.value);

    if (this.inputId) {
      element.id = this.inputId;
      this.renderer.setAttribute(element, 'id', this.inputId);
    }

    if (this.maxlength != null && this.maxlength > 0) {
      element.maxlength = this.maxlength;
      this.renderer.setAttribute(element, 'maxlength', String(this.maxlength));
    } else {
      this.renderer.removeAttribute(element, 'maxlength');
    }

    this.syncInternalInputMaxLength();
  }

  private syncInternalInputMaxLength(): void {
    const apply = (): void => {
      const input = this.getInternalInput();
      if (!input) {
        return;
      }

      if (this.maxlength != null && this.maxlength > 0) {
        input.maxLength = this.maxlength;
        this.renderer.setAttribute(input, 'maxlength', String(this.maxlength));
        if (input.value.length > this.maxlength) {
          input.value = input.value.slice(0, this.maxlength);
        }
        return;
      }

      input.removeAttribute('maxlength');
    };

    apply();
    queueMicrotask(apply);
    requestAnimationFrame(apply);
  }

  private getInternalInput(): HTMLInputElement | null {
    const host = this.elementRef.nativeElement;
    return host.shadowRoot?.querySelector('input')
      ?? host.querySelector('input');
  }

  private shouldBlockKeydown(event: KeyboardEvent): boolean {
    if (this.maxlength == null || this.maxlength <= 0) {
      return false;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return false;
    }

    const navigationKeys = new Set([
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Escape',
      'Enter',
    ]);
    if (navigationKeys.has(event.key) || event.key.length !== 1) {
      return false;
    }

    const input = this.getInternalInput();
    const currentValue = input?.value ?? this.value;
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const replacingSelection = selectionEnd > selectionStart;

    return !replacingSelection && currentValue.length >= this.maxlength;
  }

  private applyMaxLength(value: string): string {
    if (this.maxlength == null || this.maxlength <= 0) {
      return value;
    }

    return value.slice(0, this.maxlength);
  }

  private syncMaskMaxLength(element: B3DsInputTextElement): void {
    if (this.mask !== 'integer' || this.maxlength == null || this.maxlength <= 0) {
      return;
    }

    const defaultMask = CUSTOM_MASKS.integer;
    const currentMask = element.map?.['integer'] ?? defaultMask;

    element.map = {
      ...(element.map ?? {}),
      integer: {
        ...currentMask,
        pattern: new RegExp(`^\\d{0,${this.maxlength}}$`),
        placeholder: this.placeholder || defaultMask.placeholder,
      },
    };
  }

  private normalizeValue(value: string | null | undefined): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase();
  }

  private setOptionalAttribute(element: B3DsInputTextElement, name: string, value: string): void {
    if (value) {
      (element as unknown as Record<string, string>)[name] = value;
      this.renderer.setAttribute(element, name, value);
      return;
    }

    this.renderer.removeAttribute(element, name);
  }
}
